import React from "react";

export type ButtonProps = {
  expand?: boolean;
  icon?: any;
  fill?: "clear" | "outline" | "solid";
  children?: React.JSX.Element | React.JSX.Element[] | React.ReactNode;
  type?: "default" | "primary" | "secondary" | "info" | "rounded";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  direction?: "horizontal" | "vertical";
  style?: React.CSSProperties;
  name?: string;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
};

/** Get Tailwind classes for button variant (type + fill combination) */
const getVariantClasses = (
  type: ButtonProps["type"],
  fill: ButtonProps["fill"]
): string => {
  const variantMap: Record<string, Record<string, string>> = {
    primary: {
      solid: "btn-primary-solid",
      outline: "btn-primary-outline btn-outline",
      clear: "btn-primary-clear"
    },
    secondary: {
      solid: "btn-secondary-solid",
      outline: "btn-secondary-outline btn-outline",
      clear: "btn-secondary-clear"
    },
    info: {
      solid: "btn-info-solid",
      outline: "btn-info-outline btn-outline",
      clear: ""
    },
    default: {
      solid: "btn-default-solid",
      outline: "btn-default-outline btn-outline",
      clear: "btn-default-clear"
    },
    rounded: {
      solid: "btn-rounded-solid",
      outline: "",
      clear: ""
    }
  };

  return variantMap[type ?? "default"]?.[fill ?? "solid"] ?? "";
};

/** Get Tailwind classes for button size */
const getSizeClasses = (size: ButtonProps["size"]): string => {
  const sizeMap: Record<string, string> = {
    xs: "text-[0.6em]",
    sm: "text-[0.8em]",
    md: "text-[13px] font-bold",
    lg: "text-[1.2em]",
    xl: "text-[1.6em] w-4"
  };

  return sizeMap[size ?? "md"] ?? sizeMap.md;
};

export function Button({
  expand = false,
  children,
  icon,
  direction = "horizontal",
  fill = "solid",
  type = "default",
  size = "md",
  style = {},
  name = "",
  className = "",
  onClick,
  disabled = false
}: ButtonProps) {
  const directionClass = direction === "vertical" ? "flex-col" : "";
  const disabledClass = disabled ? "opacity-[0.65]" : "";
  const variantClasses = getVariantClasses(type, fill);
  const sizeClasses = getSizeClasses(size);

  const buttonElementClasses = [
    "Button__element",
    `Button__element--${direction}`,
    `Button__element--${size}`,
    `Button__element--${fill}`,
    `Button__element--${type}`,
    disabled ? "Button__element--disabled" : "",
    "btn-base",
    directionClass,
    sizeClasses,
    variantClasses,
    disabledClass,
    "outline-none focus:outline-none focus-visible:outline-2 focus-visible:outline-[var(--ring,#1fd4d4)] focus-visible:outline-offset-2 focus-visible:shadow-none"
  ]
    .filter(Boolean)
    .join(" ");

  const containerClasses = [
    "Button",
    className,
    expand ? "Button--full w-[var(--fullWidth)]" : ""
  ]
    .filter(Boolean)
    .join(" ");

  const contentClasses = [
    "Button__elementContent",
    "btn-content",
    icon && !children && !name ? "btn-icon-content" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClasses}>
      <button
        className={buttonElementClasses}
        style={style}
        onClick={onClick}
        disabled={disabled}
        tabIndex={0}
        aria-label={name}
      >
        <div className={contentClasses} aria-hidden="true">
          <span>{icon ?? ""}</span>
          <span>{children ?? name}</span>
        </div>
      </button>
    </div>
  );
}
